import { Button, Space } from 'antd';
import { FilePdfOutlined, PlusOutlined, QrcodeOutlined } from '@ant-design/icons';
import { ListHeader } from 'containers';
import { useDispatch } from 'react-redux';
import { globalActions } from 'app/services/global';
import { useGenerateQrCodesMutation } from 'app/services/tools';
import { useGetUnitQuery } from 'app/services/me';
import { Drawers } from 'app/types/drawer';
import { Tool } from 'app/types/tool';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Paths, User } from 'constants/index';
import { useHasPermission } from 'hooks/authorization/useHasPermission';
import { useState } from 'react';
import GenerateQrModal from 'components/GenerateQrModal/GenerateQrModal';
import { generateToolReportPdf } from 'utils/toolReport';
import { showError } from 'utils/notifications';
import { buildDeviceQrUrl } from 'utils/qr/parseQrValue';

type TableHeaderProps = {
  reportTools?: Tool[];
  onReportToolsChange: (tools?: Tool[]) => void;
};

const TableHeader = ({ reportTools, onReportToolsChange }: TableHeaderProps) => {
  const { t } = useTranslation();
  const canAddTool = useHasPermission(User.Permissions.ADD_TOOL);

  const dispatch = useDispatch();
  const handleClick = () => dispatch(globalActions.openDrawer({ key: Drawers.ADD_TOOL }));

  const [ isQrModalOpen, setIsQrModalOpen ] = useState(false);
  const [ generateQrCodes ] = useGenerateQrCodesMutation();

  const { data: unit } = useGetUnitQuery({});
  const [ isReportGenerating, setIsReportGenerating ] = useState(false);

  const { pathname } = useLocation();
  const showActive = pathname.includes(Paths.TOOLS_ACTIVE);
  const showToolActions = canAddTool && showActive && !reportTools;

  const handleGenerateReport = async () => {
    if (!reportTools) return;
    setIsReportGenerating(true);

    try {
      const pdf = await generateToolReportPdf(reportTools, unit?.Name, t);
      pdf.save(`kvalilog-tools-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      onReportToolsChange(undefined);
    } catch (err: any) {
      showError(t('pages.tools.report.failed'), err?.message);
    } finally {
      setIsReportGenerating(false);
    }
  };

  return (
    <ListHeader title={t('entities.tools.title')}>
      <Space wrap>
        {reportTools ? <>
          <Button onClick={() => onReportToolsChange(undefined)} disabled={isReportGenerating}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleGenerateReport} icon={<FilePdfOutlined />} type='primary' disabled={!reportTools.length} loading={isReportGenerating}>
            {t('pages.tools.report.generate', { count: reportTools.length })}
          </Button>
        </> : (
          <Button onClick={() => onReportToolsChange([])} icon={<FilePdfOutlined />}>
            {t('pages.tools.report.create')}
          </Button>
        )}
        {showToolActions && <>
          <Button onClick={() => setIsQrModalOpen(true)} icon={<QrcodeOutlined />}>
            {t('components.generateqr.title')}
          </Button>
          <Button onClick={handleClick} icon={<PlusOutlined />} type='primary'>
            {t('common.add', { type: t('entities.tools.title').toLowerCase() })}
          </Button>
        </>}
      </Space>
      {canAddTool && showActive && (
        <GenerateQrModal
          open={isQrModalOpen}
          onClose={() => setIsQrModalOpen(false)}
          generateCodes={(count) => generateQrCodes(count).unwrap()}
          buildUrl={buildDeviceQrUrl}
          fileNamePrefix="kvalilog-device-qr"
        />
      )}
    </ListHeader>
  );
};

export default TableHeader;
